
import React, { useState, useEffect } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [awaitingLeadInfo, setAwaitingLeadInfo] = useState(false);
  const [leadStep, setLeadStep] = useState(0);
  const [userData, setUserData] = useState({ name: '', phone: '', city: '' });
  const [messageCount, setMessageCount] = useState(0);
  const [quickReplies, setQuickReplies] = useState([]);

  useEffect(() => {
    if (isChatOpen && chatMessages.length === 0) {
      initChat();
    }
  }, [isChatOpen]);

  const initChat = () => {
    addChatMessage("Hi 👋 Welcome to Blue Sky Energy! How can we help you today?", 'bot');
    setTimeout(() => {
      addChatMessage("Choose an option to get started:", 'bot');
      setQuickReplies([
        "🏠 Residential Solar", 
        "🏢 Commercial Solar", 
        "💰 Pricing & Subsidy", 
        "📍 Our Service Areas"
      ]);
    }, 800);
  };

  const addChatMessage = (text, sender) => {
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const newMessage = {
      text,
      sender,
      time,
      id: Date.now()
    };
    setChatMessages(prev => [...prev, newMessage]);
    if (sender === 'user') {
      setMessageCount(prev => prev + 1);
    }
  };

  const matchKeywords = (input) => {
    const text = input.toLowerCase();
    if (text.includes('human') || text.includes('call') || text.includes('talk')) {
      return "I'd be happy to connect you with our team! 📞\n\nCall us directly: (+91) 80879 32436";
    }
    if (text.includes('commercial') || text.includes('business')) {
      return "☀️ Commercial Solar Solutions\n\nROI typically within 3-5 years. We're National Award Winners!";
    }
    if (text.includes('home') || text.includes('residential')) {
      return "🏠 Residential Solar Solutions\n\nEMI options available. As India's #1 Rooftop Player, we guarantee quality.";
    }
    if (text.includes('price') || text.includes('cost')) {
      return "💰 Typical costs:\n• 3kW: ₹1.8L - ₹2.2L\n• 5kW: ₹2.8L - ₹3.5L\n\nShall I collect your details?";
    }
    if (text.includes('contact') || text.includes('address')) {
      return "📍 Our Address:\nPlot.No 62A, Umred Rd, Satyam nagar, dighori, Bahadura, 440034\n\n📞 Phone: +91 80879 32436";
    }
    return "I understand you're interested in solar solutions! I can help with pricing, installation process, or connecting you with our team.";
  };

  const handleLeadCapture = (input) => {
    if (leadStep === 0) {
      setUserData(prev => ({ ...prev, name: input }));
      setLeadStep(1);
      addChatMessage(`Nice to meet you, ${input}! 😊\n\nCould you please share your phone number?`, 'bot');
    } else if (leadStep === 1) {
      setUserData(prev => ({ ...prev, phone: input }));
      setLeadStep(2);
      addChatMessage("Great! And which city are you from?", 'bot');
    } else if (leadStep === 2) {
      setUserData(prev => ({ ...prev, city: input }));
      setLeadCaptured(true);
      setAwaitingLeadInfo(false);
      const leadId = `lead_${Date.now()}`;
      localStorage.setItem(leadId, JSON.stringify({...userData, city: input, timestamp: new Date()}));
      addChatMessage(`Perfect! Thank you ${userData.name}! ✅\n\nOur team will contact you within 24 hours.`, 'bot');
    }
  };

  const handleChatSend = () => {
    const text = chatInput.trim();
    if (!text) return;

    addChatMessage(text, 'user');
    setChatInput('');
    setQuickReplies([]);

    setTimeout(() => {
      if (awaitingLeadInfo) {
        handleLeadCapture(text);
      } else {
        const response = matchKeywords(text);
        addChatMessage(response, 'bot');

        if (!leadCaptured && messageCount >= 1) {
          setTimeout(() => {
            addChatMessage("I'd love to help you further! 📋\n\nCan I get your name to assist you better?", 'bot');
            setAwaitingLeadInfo(true);
          }, 1500);
        }
      }
    }, 600);
  };

  const handleQuickReply = (option) => {
    setChatInput(option);
    setTimeout(() => {
      handleChatSend();
    }, 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleChatSend();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('http://localhost:5000/api/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Thank you! Your message has been sent successfully.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppClick = () => {
    const phone = '+918087932436';
    const message = 'Hello! I would like to know more about solar solutions.';
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <>
      <style>
        {`
          /* Color Palette */
          :root {
            --color-deep-teal: #061221;
            --color-dark-navy: #123249;
            --color-midnight: #2D5A74;
            --color-slate: #447793;
            --color-frost: #DDE8ED;
            --color-white: #ffffff;
            --gradient-primary: linear-gradient(135deg, #447793 0%, #2D5A74 100%);
            --gradient-hover: linear-gradient(135deg, #2D5A74 0%, #123249 100%);
            --gradient-light: linear-gradient(135deg, #DDE8ED 0%, #ffffff 100%);
            --shadow-sm: 0 2px 15px rgba(6, 18, 33, 0.08);
            --shadow-md: 0 4px 20px rgba(6, 18, 33, 0.12);
            --shadow-lg: 0 10px 30px rgba(6, 18, 33, 0.15);
          }

          /* Responsive CSS */
          @media (max-width: 1200px) {
            .container {
              max-width: 100% !important;
              padding: 30px 20px !important;
            }
            
            .heading {
              font-size: 32px !important;
              margin-bottom: 40px !important;
            }
            
            .contact-wrapper {
              grid-template-columns: 1fr !important;
              gap: 40px !important;
            }
            
            .form-grid {
              grid-template-columns: 1fr !important;
              gap: 15px !important;
            }
            
            .map-iframe {
              width: 100% !important;
              height: 350px !important;
            }
          }
          
          @media (max-width: 768px) {
            .container {
              padding: 25px 15px !important;
            }
            
            .heading {
              font-size: 28px !important;
              margin-bottom: 30px !important;
              padding-bottom: 10px !important;
            }
            
            .image-box {
              height: 200px !important;
              margin-bottom: 40px !important;
            }
            
            .image-title {
              font-size: 32px !important;
            }
            
            .form-style {
              padding: 30px 20px !important;
            }
            
            .form-title {
              font-size: 22px !important;
              margin-bottom: 25px !important;
            }
            
            .contact-card {
              padding: 20px !important;
            }
            
            .card-title {
              font-size: 16px !important;
            }
            
            .card-content {
              font-size: 15px !important;
            }
            
            .map-section {
              padding: 30px 20px !important;
            }
            
            .map-title {
              font-size: 24px !important;
              margin-bottom: 25px !important;
            }
            
            .map-container {
              height: 300px !important;
            }
            
            .live-chat {
              padding: 30px 20px !important;
            }
            
            .live-chat-title {
              font-size: 22px !important;
            }
            
            .whatsapp-button {
              padding: 12px 30px !important;
              font-size: 15px !important;
            }
            
            /* Chatbot Mobile */
            .chat-window {
              width: 90vw !important;
              right: 5vw !important;
              left: 5vw !important;
              height: 500px !important;
              bottom: 80px !important;
            }
            
            .chat-message {
              max-width: 85% !important;
              font-size: 13px !important;
              padding: 10px 14px !important;
            }
            
            .reply-btn {
              font-size: 12px !important;
              padding: 6px 12px !important;
            }
            
            .chat-input {
              font-size: 13px !important;
              padding: 10px 14px !important;
            }
            
            .send-btn {
              width: 40px !important;
              height: 40px !important;
              font-size: 14px !important;
            }
            
            .chat-toggle-btn {
              width: 55px !important;
              height: 55px !important;
              font-size: 22px !important;
            }
            
            .breadcrumb {
              font-size: 13px !important;
              margin-bottom: 25px !important;
            }
          }
          
          @media (max-width: 576px) {
            .heading {
              font-size: 24px !important;
            }
            
            .image-box {
              height: 150px !important;
            }
            
            .image-title {
              font-size: 24px !important;
            }
            
            .form-style {
              padding: 25px 15px !important;
            }
            
            .form-title {
              font-size: 20px !important;
            }
            
            .input-style,
            .textarea-style {
              padding: 10px 14px !important;
              font-size: 14px !important;
            }
            
            .textarea-style {
              min-height: 100px !important;
            }
            
            .submit-button {
              padding: 12px 25px !important;
              font-size: 15px !important;
            }
            
            .contact-info {
              gap: 20px !important;
            }
            
            .map-title {
              font-size: 20px !important;
            }
            
            .map-container {
              height: 250px !important;
            }
            
            .map-iframe {
              height: 250px !important;
            }
            
            .live-chat {
              padding: 25px 15px !important;
            }
            
            .live-chat-title {
              font-size: 20px !important;
            }
            
            .chat-window {
              width: 95vw !important;
              right: 2.5vw !important;
              left: 2.5vw !important;
              height: 450px !important;
            }
          }
          
          @media (max-width: 480px) {
            .container {
              padding: 20px 10px !important;
            }
            
            .heading {
              font-size: 22px !important;
            }
            
            .image-box {
              height: 120px !important;
              margin-bottom: 30px !important;
            }
            
            .image-title {
              font-size: 20px !important;
              letter-spacing: 1px !important;
            }
            
            .form-grid {
              gap: 12px !important;
              margin-bottom: 15px !important;
            }
            
            .contact-card {
              padding: 15px !important;
            }
            
            .badge {
              font-size: 12px !important;
              padding: 5px 10px !important;
            }
            
            .whatsapp-button {
              padding: 10px 25px !important;
              font-size: 14px !important;
            }
            
            .chat-window {
              height: 400px !important;
            }
          }
          
          /* Touch-friendly styles */
          @media (hover: none) and (pointer: coarse) {
            .contact-card:hover,
            .submit-button:hover,
            .whatsapp-button:hover,
            .chat-toggle-btn:hover {
              transform: none !important;
            }
          }
          
          /* Prevent zoom on mobile input */
          @media (max-width: 768px) {
            input, textarea, select {
              font-size: 16px !important;
            }
          }
          
          /* Custom animations */
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
          
          .animate-pulse {
            animation: pulse 2s infinite;
          }
          
          /* Smooth transitions */
          .contact-card,
          .submit-button,
          .whatsapp-button,
          .image-box {
            transition: all 0.3s ease;
          }
        `}
      </style>

      <div className="container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '40px 20px',
        fontFamily: "Cambria, serif",
        position: 'relative',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Breadcrumb - Blue Theme */}
        <div className="breadcrumb" style={{
          fontSize: '14px',
          color: '#2D5A74',
          marginBottom: '30px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <span>Contact Us</span>
          <span style={{color: '#DDE8ED'}}>›</span>
          <span>Home</span>
          <span style={{color: '#DDE8ED'}}>›</span>
          <span style={{color: '#447793', fontWeight: '500'}}>Contact details</span>
        </div>

        {/* Main Heading - Blue Theme */}
        <h1 className="heading" style={{
          fontSize: '36px',
          fontWeight: '700',
          color: '#123249',
          marginBottom: '50px',
          position: 'relative',
          paddingBottom: '15px'
        }}>
          Get in Touch
          <span style={{
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: '80px',
            height: '4px',
            background: 'linear-gradient(90deg, #447793, #2D5A74)',
            borderRadius: '2px'
          }}></span>
        </h1>

        {/* Image Box - Blue Theme */}
        <div 
          className="image-box"
          style={{
            background: `linear-gradient(rgba(18, 50, 73, 0.9), rgba(45, 90, 116, 0.9)), url('/images/contact-bg.jpg') center/cover no-repeat`,
            height: '300px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            marginBottom: '60px',
            position: 'relative'
          }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <h2 className="image-title" style={{
            fontSize: '42px',
            fontWeight: '800',
            color: 'white',
            textAlign: 'center',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            textShadow: '2px 2px 10px rgba(6, 18, 33, 0.3)',
            zIndex: '2',
            padding: '0 20px'
          }}>Contact Us Today</h2>
        </div>

        {/* Contact Form & Info - Blue Theme */}
        <div className="contact-wrapper" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          marginBottom: '80px',
          width: '100%'
        }}>
          {/* Left: Contact Form */}
          <div>
            <div className="form-style" style={{
              background: '#DDE8ED',
              padding: '40px',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(6, 18, 33, 0.08)',
              border: '1px solid #DDE8ED',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <h2 className="form-title" style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#123249',
                marginBottom: '30px'
              }}>Send Us a Message</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#123249',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      Name <span style={{ color: '#447793' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="input-style"
                      style={{
                        padding: '12px 16px',
                        border: '2px solid #DDE8ED',
                        borderRadius: '6px',
                        fontSize: '15px',
                        transition: 'all 0.3s ease',
                        outline: 'none',
                        width: '100%',
                        boxSizing: 'border-box',
                        backgroundColor: 'white',
                        color: '#123249'
                      }}
                      placeholder="Your name"
                      onFocus={(e) => e.target.style.borderColor = '#447793'}
                      onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
                    />
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#123249',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      Email <span style={{ color: '#447793' }}>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input-style"
                      style={{
                        padding: '12px 16px',
                        border: '2px solid #DDE8ED',
                        borderRadius: '6px',
                        fontSize: '15px',
                        transition: 'all 0.3s ease',
                        outline: 'none',
                        width: '100%',
                        boxSizing: 'border-box',
                        backgroundColor: 'white',
                        color: '#123249'
                      }}
                      placeholder="your@email.com"
                      onFocus={(e) => e.target.style.borderColor = '#447793'}
                      onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
                    />
                  </div>
                </div>

                <div className="form-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#123249',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      Phone <span style={{ color: '#447793' }}>*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="input-style"
                      style={{
                        padding: '12px 16px',
                        border: '2px solid #DDE8ED',
                        borderRadius: '6px',
                        fontSize: '15px',
                        transition: 'all 0.3s ease',
                        outline: 'none',
                        width: '100%',
                        boxSizing: 'border-box',
                        backgroundColor: 'white',
                        color: '#123249'
                      }}
                      placeholder="+91 "
                      onFocus={(e) => e.target.style.borderColor = '#447793'}
                      onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
                    />
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#123249',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      Subject <span style={{ color: '#447793' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="input-style"
                      style={{
                        padding: '12px 16px',
                        border: '2px solid #DDE8ED',
                        borderRadius: '6px',
                        fontSize: '15px',
                        transition: 'all 0.3s ease',
                        outline: 'none',
                        width: '100%',
                        boxSizing: 'border-box',
                        backgroundColor: 'white',
                        color: '#123249'
                      }}
                      placeholder="Subject"
                      onFocus={(e) => e.target.style.borderColor = '#447793'}
                      onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#123249',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    Message <span style={{ color: '#447793' }}>*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="textarea-style"
                    style={{
                      padding: '12px 16px',
                      border: '2px solid #DDE8ED',
                      borderRadius: '6px',
                      fontSize: '15px',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                      minHeight: '120px',
                      resize: 'vertical',
                      width: '100%',
                      boxSizing: 'border-box',
                      fontFamily: "Cambria, serif",
                      backgroundColor: 'white',
                      color: '#123249'
                    }}
                    placeholder="Your message..."
                    onFocus={(e) => e.target.style.borderColor = '#447793'}
                    onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
                  />
                </div>

                <button 
                  type="submit" 
                  className="submit-button"
                  style={{
                    background: 'linear-gradient(135deg, #447793, #2D5A74)',
                    color: 'white',
                    border: 'none',
                    padding: '15px 30px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    width: '100%',
                    marginTop: '20px'
                  }}
                  disabled={isSubmitting}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #2D5A74, #123249)';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 10px 25px rgba(45, 90, 116, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #447793, #2D5A74)';
                    e.target.style.transform = 'none';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Submit Now'}
                </button>
              </form>
            </div>
          </div>

          {/* Right: Contact Information - Blue Theme */}
          <div className="contact-info" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '30px'
          }}>
            <div className="contact-card" style={{
              background: 'white',
              padding: '25px',
              borderRadius: '10px',
              boxShadow: '0 5px 15px rgba(6, 18, 33, 0.05)',
              border: '1px solid #DDE8ED',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 15px 30px rgba(68, 119, 147, 0.1)';
              e.currentTarget.style.borderColor = '#447793';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 5px 15px rgba(6, 18, 33, 0.05)';
              e.currentTarget.style.borderColor = '#DDE8ED';
            }}>
              <h3 className="card-title" style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#123249',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>📞 Phone Number</h3>
              <p className="card-content" style={{
                fontSize: '16px',
                color: '#2D5A74',
                lineHeight: '1.6'
              }}>+91 80879 32436</p>
            </div>

            <div className="contact-card" style={{
              background: 'white',
              padding: '25px',
              borderRadius: '10px',
              boxShadow: '0 5px 15px rgba(6, 18, 33, 0.05)',
              border: '1px solid #DDE8ED',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 15px 30px rgba(68, 119, 147, 0.1)';
              e.currentTarget.style.borderColor = '#447793';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 5px 15px rgba(6, 18, 33, 0.05)';
              e.currentTarget.style.borderColor = '#DDE8ED';
            }}>
              <h3 className="card-title" style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#123249',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>✉️ Email Address</h3>
              <p className="card-content" style={{
                fontSize: '16px',
                color: '#2D5A74',
                lineHeight: '1.6'
              }}>info@blueskyenergy.in</p>
            </div>

            <div className="contact-card" style={{
              background: 'white',
              padding: '25px',
              borderRadius: '10px',
              boxShadow: '0 5px 15px rgba(6, 18, 33, 0.05)',
              border: '1px solid #DDE8ED',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 15px 30px rgba(68, 119, 147, 0.1)';
              e.currentTarget.style.borderColor = '#447793';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 5px 15px rgba(6, 18, 33, 0.05)';
              e.currentTarget.style.borderColor = '#DDE8ED';
            }}>
              <div className="badge" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: '#DDE8ED',
                color: '#123249',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '500',
                marginBottom: '10px'
              }}>
                <span>●</span> OPEN NOW
              </div>
              <h3 className="card-title" style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#123249',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>⏰ Business Hours</h3>
              <p className="card-content" style={{
                fontSize: '16px',
                color: '#2D5A74',
                lineHeight: '1.6'
              }}>Sun-Sat: 09:30 AM - 09:00 PM</p>
            </div>

            <div className="contact-card" style={{
              background: 'white',
              padding: '25px',
              borderRadius: '10px',
              boxShadow: '0 5px 15px rgba(6, 18, 33, 0.05)',
              border: '1px solid #DDE8ED',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 15px 30px rgba(68, 119, 147, 0.1)';
              e.currentTarget.style.borderColor = '#447793';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 5px 15px rgba(6, 18, 33, 0.05)';
              e.currentTarget.style.borderColor = '#DDE8ED';
            }}>
              <h3 className="card-title" style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#123249',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>🌐 Other Website</h3>
              <p className="card-content" style={{
                fontSize: '16px',
                color: '#2D5A74',
                lineHeight: '1.6'
              }}>Visit our other website</p>
            </div>

            <div className="contact-card" style={{
              background: 'white',
              padding: '25px',
              borderRadius: '10px',
              boxShadow: '0 5px 15px rgba(6, 18, 33, 0.05)',
              border: '1px solid #DDE8ED',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 15px 30px rgba(68, 119, 147, 0.1)';
              e.currentTarget.style.borderColor = '#447793';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 5px 15px rgba(6, 18, 33, 0.05)';
              e.currentTarget.style.borderColor = '#DDE8ED';
            }}>
              <h3 className="card-title" style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#123249',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>📍 Address</h3>
              <p className="card-content" style={{
                fontSize: '16px',
                color: '#2D5A74',
                lineHeight: '1.6'
              }}>
                Plot.No 62A, Umred Rd, Satyam nagar, dighori, Bahadura, 440034
              </p>
            </div>
          </div>
        </div>

        {/* Map Section - Blue Theme */}
        <div className="map-section" style={{
          background: '#DDE8ED',
          padding: '50px',
          borderRadius: '12px',
          marginBottom: '60px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <h2 className="map-title" style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#123249',
            marginBottom: '30px',
            textAlign: 'center'
          }}>Our Location</h2>
          <div className="map-container" style={{
            height: '400px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#2D5A74',
            fontSize: '18px',
            width: '100%',
            overflow: 'hidden'
          }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d35602.40292825899!2d79.14732526803402!3d21.104499928073757!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bd4b970c1612117%3A0x217cca3b13c6dbb7!2sBlue%20sky%20energy!5e0!3m2!1sen!2sin!4v1766398621132!5m2!1sen!2sin"
              className="map-iframe"
              style={{ border: 0, width: '100%', height: '100%' }}
              allowFullScreen={true}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Google Maps Location"
            ></iframe>
          </div>
        </div>

        {/* Live Chat Section - Blue Theme */}
        <div className="live-chat" style={{
          background: 'linear-gradient(135deg, #447793, #2D5A74)',
          color: 'white',
          padding: '40px',
          borderRadius: '12px',
          textAlign: 'center',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <h3 className="live-chat-title" style={{
            fontSize: '24px',
            fontWeight: '700',
            marginBottom: '15px',
            color: 'white'
          }}>Live Chat</h3>
          <p className="live-chat-text" style={{
            fontSize: '16px',
            marginBottom: '25px',
            opacity: '0.9',
            color: '#DDE8ED'
          }}>
            Connect over WhatsApp to resolve your queries real-time.
          </p>
          <button 
            className="whatsapp-button"
            style={{
              background: '#447793',
              color: 'white',
              border: 'none',
              padding: '15px 40px',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px'
            }}
            onClick={handleWhatsAppClick}
            onMouseEnter={(e) => {
              e.target.style.background = '#2D5A74';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 10px 25px rgba(45, 90, 116, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#447793';
              e.target.style.transform = 'none';
              e.target.style.boxShadow = 'none';
            }}
          >
            💬 Live WhatsApp Chat
          </button>
        </div>
      </div>

      {/* Chatbot Component - Blue Theme */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: '9999'
      }}>
        {/* Chat Toggle Button */}
        <button 
          className="chat-toggle-btn"
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '55px',
            height: '55px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #447793, #2D5A74)',
            color: 'white',
            border: 'none',
            fontSize: '22px',
            cursor: 'pointer',
            boxShadow: '0 8px 30px rgba(68, 119, 147, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '10000',
            padding: '0',
            transition: 'all 0.3s ease'
          }}
          onClick={() => setIsChatOpen(!isChatOpen)}
          onMouseEnter={(e) => {
            e.target.style.background = 'linear-gradient(135deg, #2D5A74, #123249)';
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'linear-gradient(135deg, #447793, #2D5A74)';
            e.target.style.transform = 'none';
          }}
        >
          💬
        </button>

        {/* Chat Window */}
        <div className="chat-window" style={{
          position: 'fixed',
          bottom: '85px',
          right: '20px',
          width: '350px',
          height: '500px',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(6, 18, 33, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: '9999',
          transition: 'all 0.3s ease',
          opacity: isChatOpen ? '1' : '0',
          visibility: isChatOpen ? 'visible' : 'hidden',
          transform: isChatOpen ? 'translateY(0)' : 'translateY(20px)',
          border: '1px solid #DDE8ED'
        }}>
          {/* Chat Header */}
          <div style={{
            background: 'linear-gradient(135deg, #447793, #2D5A74)',
            color: 'white',
            padding: '15px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.2)',
                padding: '8px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                fontSize: '16px'
              }}>
                🏆
              </div>
              <div>
                <h3 style={{ margin: '0', fontSize: '15px', fontWeight: '600', color: 'white' }}>
                  Blue Sky Energy
                </h3>
                <p style={{ margin: '0', fontSize: '11px', opacity: '0.9', color: '#DDE8ED' }}>
                  National Award Winner
                </p>
              </div>
            </div>
            <button 
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '5px'
              }}
              onClick={() => setIsChatOpen(false)}
            >
              ✕
            </button>
          </div>

          {/* Quick Replies */}
          {quickReplies.length > 0 && (
            <div className="quick-replies" style={{
              padding: '12px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              background: '#DDE8ED',
              borderBottom: '1px solid #DDE8ED'
            }}>
              {quickReplies.map((option, index) => (
                <button
                  key={index}
                  className="reply-btn"
                  style={{
                    background: 'white',
                    border: '1px solid #447793',
                    color: '#123249',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                    fontWeight: '500'
                  }}
                  onClick={() => handleQuickReply(option)}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#447793';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'white';
                    e.target.style.color = '#123249';
                    e.target.style.transform = 'none';
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {/* Chat Messages */}
          <div className="chat-messages" style={{
            flex: '1',
            padding: '15px',
            background: '#DDE8ED',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            {chatMessages.map((msg) => (
              <div 
                key={msg.id} 
                className="chat-message"
                style={{
                  maxWidth: '80%',
                  padding: '10px 14px',
                  borderRadius: '18px',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-line',
                  wordWrap: 'break-word',
                  position: 'relative',
                  ...(msg.sender === 'bot' ? {
                    background: 'white',
                    alignSelf: 'flex-start',
                    borderBottomLeftRadius: '4px',
                    boxShadow: '0 2px 8px rgba(6, 18, 33, 0.08)',
                    color: '#123249',
                    border: '1px solid #DDE8ED'
                  } : {
                    background: 'linear-gradient(135deg, #447793, #2D5A74)',
                    color: 'white',
                    alignSelf: 'flex-end',
                    borderBottomRightRadius: '4px'
                  })
                }}
              >
                {msg.text}
                <span style={{
                  fontSize: '11px',
                  marginTop: '6px',
                  display: 'block',
                  opacity: '0.7'
                }}>
                  {msg.time}
                </span>
              </div>
            ))}
          </div>

          {/* Chat Input */}
          <div style={{
            padding: '15px',
            borderTop: '1px solid #DDE8ED',
            background: 'white'
          }}>
            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '8px'
            }}>
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="chat-input"
                style={{
                  flex: '1',
                  padding: '12px 16px',
                  border: '1px solid #DDE8ED',
                  borderRadius: '25px',
                  outline: 'none',
                  fontSize: '14px',
                  fontFamily: "Cambria, serif",
                  color: '#123249'
                }}
                onFocus={(e) => e.target.style.borderColor = '#447793'}
                onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
              />
              <button 
                className="send-btn"
                style={{
                  background: 'linear-gradient(135deg, #447793, #2D5A74)',
                  color: 'white',
                  border: 'none',
                  width: '45px',
                  height: '45px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  transition: 'all 0.2s ease'
                }}
                onClick={handleChatSend}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #2D5A74, #123249)';
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #447793, #2D5A74)';
                  e.target.style.transform = 'none';
                }}
              >
                📤
              </button>
            </div>
            <p style={{
              textAlign: 'center',
              fontSize: '11px',
              color: '#2D5A74',
              margin: '0'
            }}>
              Powered by Blue Sky Energy
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;